import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MapPin, Building2, LayoutGrid, Sparkles, Leaf, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { MediaTile, Lightbox } from "@/components/GalleryMedia";
import { StatusBadge } from "@/components/StatusBadge";
import { ICON_MAP } from "@/data/highlightIcons";
import { BRAND } from "@/data/site";

const HL_ICONS = [Building2, LayoutGrid, Leaf, Sparkles];
const PREVIEW = 8;

function ProjectGallery({ projectId }) {
  const [media, setMedia] = useState([]);
  const [index, setIndex] = useState(null);
  useEffect(() => {
    setMedia([]);
    api.get(`/gallery?project_id=${encodeURIComponent(projectId)}`).then((r) => setMedia(r.data)).catch(() => {});
  }, [projectId]);
  if (!media.length) return null;
  const shown = media.slice(0, PREVIEW);
  const videos = media.filter((m) => m.type === "video").length;
  const photos = media.length - videos;
  const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
  const countLabel = [photos && plural(photos, "photo"), videos && plural(videos, "video")].filter(Boolean).join(" · ");
  const more = media.length - PREVIEW;
  return (
    <section className="mt-14" data-testid="detail-gallery-section">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal mb-1">Media</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Project Gallery</h2>
          <p className="mt-1 text-sm text-slate-500" data-testid="detail-gallery-count">{countLabel}</p>
        </div>
        <Link to={`/gallery?project=${projectId}`} data-testid="detail-gallery-folder-link" className="group inline-flex items-center gap-2 rounded-full border border-navy/30 px-5 py-2.5 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors">
          <Images className="h-4 w-4" /> Open full folder <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
      <div className="grid gap-3 sm:gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4" data-testid="detail-gallery-grid">
        {shown.map((m, i) => (
          <Reveal key={m.id} delay={(i % 4) * 0.05} className="relative">
            <MediaTile m={m} onOpen={() => setIndex(i)} />
            {more > 0 && i === PREVIEW - 1 && (
              <Link to={`/gallery?project=${projectId}`} data-testid="detail-gallery-more"
                className="absolute inset-0 flex items-center justify-center rounded-xl bg-navy-void/70 font-display text-2xl font-bold text-white hover:bg-navy-void/80 transition-colors">
                +{more} more
              </Link>
            )}
          </Reveal>
        ))}
      </div>
      <Lightbox items={media} index={index} setIndex={setIndex} />
    </section>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  useSeo("projects", project ? {
    title: `${project.name}${project.location ? ` – ${project.location}` : ""} | VG Homes`,
    description: (project.subtitle || project.description || "").slice(0, 158),
    image: project.image,
  } : null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImg(0);
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => setProject(false));
  }, [id]);

  const images = useMemo(() => {
    if (!project) return [];
    const all = [project.image, ...(project.images || [])].filter(Boolean);
    return [...new Set(all)];
  }, [project]);

  const nextProject = useMemo(() => {
    if (!projects.length) return null;
    const idx = projects.findIndex((p) => p.id === id);
    if (idx === -1) return null;
    return projects[(idx + 1) % projects.length];
  }, [projects, id]);

  if (project === false) return <div className="min-h-[60vh] flex items-center justify-center text-slate-400">Project not found. <Link to="/projects" className="text-navy font-semibold ml-2">Back to Projects</Link></div>;
  if (!project) return <div className="min-h-[60vh] flex items-center justify-center text-slate-400">Loading...</div>;

  const highlights = project.highlights || [];

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-8">
        <Link to="/projects" data-testid="detail-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-teal transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Projects
        </Link>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        <div className="grid gap-8 lg:grid-cols-2 items-start">
          {/* Gallery */}
          <Reveal>
            <div className="rounded-3xl overflow-hidden bg-white shadow-sm ring-1 ring-slate-100">
              <div className="relative aspect-[4/3] bg-slate-100">
                <motion.img key={activeImg} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
                  src={images[activeImg]} alt={project.name} className="h-full w-full object-cover" data-testid="detail-main-image" />
                {images.length > 1 && (
                  <>
                    <button onClick={() => setActiveImg((activeImg - 1 + images.length) % images.length)} data-testid="detail-prev-image"
                      className="absolute left-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-navy shadow hover:bg-white transition-colors"><ChevronLeft className="h-5 w-5" /></button>
                    <button onClick={() => setActiveImg((activeImg + 1) % images.length)} data-testid="detail-next-image"
                      className="absolute right-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-navy shadow hover:bg-white transition-colors"><ChevronRight className="h-5 w-5" /></button>
                  </>
                )}
                <div className="absolute top-4 left-4"><StatusBadge status={project.status} /></div>
              </div>
            </div>
            {images.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2" data-testid="detail-thumbnails">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} data-testid={`detail-thumb-${i}`}
                    className={`overflow-hidden rounded-lg aspect-square ring-2 transition-all ${i === activeImg ? "ring-teal" : "ring-transparent opacity-70 hover:opacity-100"}`}>
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </Reveal>

          {/* Details */}
          <Reveal delay={0.1}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-3">{project.type || "Residential Apartment"}</p>
            <h1 className="font-playfair text-4xl sm:text-5xl font-bold text-slate-900 leading-tight" data-testid="detail-project-name">{project.name}</h1>
            <p className="mt-3 flex items-center gap-1.5 text-slate-500"><MapPin className="h-4 w-4 text-teal" /> {project.location}</p>
            <div className="mt-5 h-1 w-16 rounded bg-teal" />

            {project.subtitle && <h2 className="mt-6 font-playfair text-2xl font-semibold text-slate-800">{project.subtitle}</h2>}
            <p className="mt-4 text-slate-600 leading-relaxed">{project.description}</p>

            {project.status === "Ongoing" && (
              <div className="mt-6 max-w-sm">
                <div className="flex justify-between text-xs text-slate-500 mb-1"><span>Construction Progress</span><span className="font-semibold text-teal-dark">{project.progress_percentage}%</span></div>
                <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full rounded-full bg-teal transition-all" style={{ width: `${project.progress_percentage}%` }} /></div>
              </div>
            )}

            <div className="mt-8 grid grid-cols-2 gap-4 rounded-2xl bg-white p-5 ring-1 ring-slate-100">
              <Fact label="Configuration" value={project.units} />
              <Fact label="Completion" value={project.completion} />
              <Fact label="Status" value={project.status} />
              <Fact label="Location" value={project.location} />
            </div>

            {highlights.length > 0 && (
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-slate-200 pt-6" data-testid="detail-highlights">
                {highlights.map((h, i) => {
                  const label = typeof h === "string" ? h : h.label;
                  const Icon = ICON_MAP[typeof h === "string" ? null : h.icon] || HL_ICONS[i % HL_ICONS.length];
                  return (
                    <div key={i} className="text-center">
                      <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-teal/10 text-teal"><Icon className="h-5 w-5" /></div>
                      <p className="mt-2 text-xs font-medium text-slate-600 leading-tight">{label}</p>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-dark transition-colors">Enquire Now</Link>
              <a href={BRAND.whatsapp} target="_blank" rel="noreferrer" className="rounded-full border border-navy/30 px-6 py-3 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors">WhatsApp Us</a>
            </div>
          </Reveal>
        </div>

        <ProjectGallery projectId={id} />

        {/* Next project nav */}
        {nextProject && nextProject.id !== id && (
          <div className="mt-16 border-t border-slate-200 pt-8 flex items-center justify-between">
            <Link to="/projects" className="text-sm font-semibold text-slate-500 hover:text-navy">All Projects</Link>
            <button onClick={() => nav(`/projects/${nextProject.id}`)} data-testid="detail-next-project"
              className="group inline-flex items-center gap-4 text-right">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400">Next Project</div>
                <div className="font-display text-lg font-semibold text-navy group-hover:text-teal transition-colors">{nextProject.name}</div>
              </div>
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-navy text-white group-hover:bg-teal transition-colors"><ArrowRight className="h-5 w-5" /></span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-slate-800 font-semibold">{value || "—"}</div>
    </div>
  );
}
